import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Button, Stepper, Step, StepLabel,
  Table, TableBody, TableCell, TableRow, Skeleton, Alert
} from '@mui/material';
import api from '../services/api';

const getActiveStep = (order) => {
  if (order.status === 'delivered') return 3;
  if (order.status === 'shipping') return 2;
  if (order.payment_status === 'paid') return 1;
  return 0;
};

const OrderStatus = () => {
  const { t } = useTranslation('order');
  const { orderNo } = useParams();
  const navigate = useNavigate();

  const statusSteps = [t('status.status_pending_payment'), t('status.status_paid'), t('status.status_shipping'), t('status.status_delivered')];
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/orders/${orderNo}`);
        setOrder(response.data);
      } catch (err) {
        setError(t('status.not_found'));
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderNo]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Skeleton height={60} />
        <Skeleton height={100} />
        <Skeleton height={200} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center', py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          {t('status.back_home')}
        </Button>
      </Box>
    );
  }

  const activeStep = getActiveStep(order);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <span data-testid="order-active-step" style={{ display: 'none' }}>{activeStep}</span>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {t('status.title')}
      </Typography>

      {/* 訂單進度 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {statusSteps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* 待付款提醒 */}
      {order.payment_status === 'unpaid' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('status.payment_reminder')}<br />
          {t('status.payment_code')}: <strong>{order.payment_code || t('status.generating')}</strong><br />
          {t('status.payment_deadline')}: {order.payment_deadline
            ? new Date(order.payment_deadline).toLocaleString('zh-TW')
            : t('status.within_7_days')}
        </Alert>
      )}

      {/* 訂單資訊 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>{t('status.order_info')}</Typography>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: 120 }}>{t('status.order_no')}</TableCell>
              <TableCell>{order.order_no}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('status.order_date')}</TableCell>
              <TableCell>{new Date(order.created_at).toLocaleString('zh-TW')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('status.order_amount')}</TableCell>
              <TableCell>
                <Typography color="error" fontWeight="bold">
                  ${order.total_amount}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('status.payment_status')}</TableCell>
              <TableCell>
                {order.payment_status === 'paid' ? (
                  <Typography color="success.main">{t('status.status_paid')}</Typography>
                ) : (
                  <Typography color="warning.main">{t('status.status_pending_payment')}</Typography>
                )}
              </TableCell>
            </TableRow>
            {order.paid_at && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('status.paid_at')}</TableCell>
                <TableCell>{new Date(order.paid_at).toLocaleString('zh-TW')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* 取貨資訊 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>{t('status.pickup_info')}</Typography>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: 120 }}>{t('status.recipient')}</TableCell>
              <TableCell>{order.customer_name}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('status.contact_phone')}</TableCell>
              <TableCell>{order.customer_phone}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('status.pickup_store')}</TableCell>
              <TableCell>
                {order.store_type} - {order.store_name}<br />
                <Typography variant="body2" color="text.secondary">
                  {order.store_address}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      {/* 商品明細 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>{t('status.items')}</Typography>
        {order.items?.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
            <Box>
              <Typography>{item.product_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                ${item.price} x {item.quantity}
              </Typography>
            </Box>
            <Typography fontWeight="bold">${item.subtotal}</Typography>
          </Box>
        ))}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t('status.total')}</Typography>
          <Typography variant="h6" color="error" fontWeight="bold">
            ${order.total_amount}
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ textAlign: 'center' }}>
        <Button variant="contained" onClick={() => navigate('/')}>
          {t('status.back_home')}
        </Button>
      </Box>
    </Box>
  );
};

export default OrderStatus;
